import { useState } from "react";
import { Copy, Moon, Square } from "lucide-react";
import { ApiError, api, type CycleResponse, type PositionResponse } from "../../api/client";
import { CycleStopError } from "../../hooks/useActiveCycle";
import PnlValue from "../PnlValue";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import Panel from "@/components/common/Panel";
import RiskBudgetBar from "@/components/common/RiskBudgetBar";
import { BiasBadge } from "@/components/common/SemanticBadge";
import { financingEur, legStopoutEur } from "@/lib/cockpit";
import OperatorBuyControl from "./OperatorBuyControl";
import type { OperatorEntryState } from "../../hooks/useOperatorEntry";

interface ActiveCycleCockpitProps {
  cycle: CycleResponse | null;
  /** Toutes les positions du cycle actif (ouvertes + fermées). Null tant que non chargées. */
  cyclePositions: PositionResponse[] | null;
  /** Mid live — alimente le P&L latent. Null sans tick. */
  mid?: number | null;
  /** True pendant un start/stop en vol. */
  pending: boolean;
  onStop: () => Promise<CycleResponse>;
  /** Entrée opérateur « Acheter maintenant » (2026-10-22) — absent ⇒ pas de bouton. */
  operatorEntry?: OperatorEntryState;
}

/**
 * LE panneau cycle — fusion de l'ex-trio CyclePanel / RiskPanel / cockpit (un fait = une
 * maison) : identité + arrêt dans l'en-tête, trois KPI héros (latent / réalisé / si-stops),
 * UNE jauge de risque §7. Valeurs inconnues → "—", jamais un faux zéro ({@link PnlValue}).
 * Le financement ne s'affiche que s'il est non nul (livre intraday ⇒ rien).
 */
export default function ActiveCycleCockpit({
  cycle,
  cyclePositions,
  mid,
  pending,
  onStop,
  operatorEntry,
}: ActiveCycleCockpitProps) {
  const [error, setError] = useState<string | null>(null);

  if (cycle == null) {
    return (
      <Panel title="Cycle" aria-label="Cycle actif" aside="—">
        <p className="text-muted-foreground text-sm">Aucun cycle actif.</p>
      </Panel>
    );
  }

  const open = (cyclePositions ?? []).filter((p) => p.closedAt == null);
  const closed = (cyclePositions ?? []).filter((p) => p.closedAt != null);

  // WCL §7 canonique du backend (profit-aware) — PAS une somme |entrée − stop|.
  const worstCaseLoss = cycle.worstCaseLossEur ?? null;
  const budget = cycle.riskBudgetEur ?? null;
  const usedPct =
    worstCaseLoss == null || budget == null || budget === 0
      ? null
      : Math.min(100, (worstCaseLoss / budget) * 100);
  const headroom =
    worstCaseLoss == null || budget == null ? null : Math.max(0, budget - worstCaseLoss);

  const closedKnown = closed.filter(
    (p) => p.realizedPnlEur != null && Number.isFinite(Number(p.realizedPnlEur)),
  );
  const realized =
    closed.length === 0
      ? null
      : closedKnown.reduce((acc, p) => acc + Number(p.realizedPnlEur), 0);
  const pnlComplete = closed.length === 0 ? null : closedKnown.length === closed.length;
  const synthetic = closed.some((p) => p.exitReason === "RECONCILED_EXTERNALLY_CLOSED");

  // Latent live sur les positions OUVERTES : Σ(mid − entrée)·dir·taille. Null sans tick.
  const unrealized =
    mid == null || open.length === 0
      ? null
      : open.reduce((acc, p) => {
          const sign = p.direction === "BUY" ? 1 : -1;
          return acc + (mid - Number(p.entryPrice)) * sign * Number(p.sizePerPointEur);
        }, 0);

  // P&L signé si TOUS les stops ouverts déclenchaient maintenant — peut être positif
  // (jambes verrouillées en profit) alors même que le WCL (plancher 0/jambe) est grand.
  const stopoutPnl = open.length === 0 ? null : open.reduce((acc, p) => acc + legStopoutEur(p), 0);

  const ctx = cycle.cockpitContext ?? null;
  const now = Date.now();
  const financing =
    ctx == null || open.length === 0
      ? null
      : open.reduce(
          (acc, p) => acc + financingEur(p, now, ctx.financingBaseRatePct, ctx.financingEnabled),
          0,
        );

  const handleStop = async () => {
    setError(null);
    try {
      await onStop();
    } catch (e) {
      setError(
        e instanceof CycleStopError ? humanStopError(e.reason) : "Impossible d'arrêter le cycle.",
      );
    }
  };

  const uid = cycle.cycleUid ? cycle.cycleUid.slice(0, 8) : `#${cycle.id}`;

  return (
    <Panel
      title="Cycle"
      aria-label="Cycle actif"
      testId="cockpit"
      tone="accent"
      aside={
        <div className="flex items-center gap-2">
          <BiasBadge bias={cycle.bias} />
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 font-mono"
            title="Copier l'UID du cycle"
            onClick={() => {
              if (cycle.cycleUid) void navigator.clipboard?.writeText(cycle.cycleUid);
            }}
            data-testid="cockpit-uid"
          >
            <code>{uid}…</code>
            <Copy className="size-3" aria-hidden />
          </button>
          <span className="tabular-nums" data-testid="cockpit-timing">
            {formatTime(cycle.startedAt)} · {formatDuration(cycle.startedAt, cycle.endedAt)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleStop}
            disabled={pending}
            data-testid="cockpit-stop"
          >
            <Square className="size-3.5" aria-hidden />
            {pending ? "Arrêt…" : "Arrêter"}
          </Button>
        </div>
      }
      bodyClassName="flex flex-col gap-4"
    >
      {/* ── trois chiffres héros ── */}
      <div className="grid grid-cols-3 gap-4">
        <Kpi label="Latent">
          {mid == null ? (
            <span className="text-muted-foreground" data-testid="cockpit-unrealized-pnl">
              —
            </span>
          ) : (
            <PnlValue
              value={unrealized}
              pnlComplete
              synthetic={false}
              className="text-2xl font-bold"
              testId="cockpit-unrealized-pnl"
            />
          )}
        </Kpi>
        <Kpi
          label="Réalisé"
          sub={closed.length > 0 ? `${closed.length} fermée${closed.length > 1 ? "s" : ""}` : undefined}
        >
          <PnlValue
            value={realized}
            pnlComplete={pnlComplete}
            synthetic={synthetic}
            className="text-2xl font-bold"
            testId="cockpit-realized-pnl"
          />
        </Kpi>
        <Kpi label="Si stops maintenant">
          {stopoutPnl == null ? (
            <span className="text-muted-foreground" data-testid="cockpit-stopout-pnl">
              —
            </span>
          ) : (
            <PnlValue
              value={stopoutPnl}
              pnlComplete
              synthetic={false}
              className="text-2xl font-bold"
              testId="cockpit-stopout-pnl"
            />
          )}
        </Kpi>
      </div>

      {/* ── risque §7 : une jauge, une ligne ── */}
      <div className="flex flex-col gap-1.5">
        <RiskBudgetBar budget={budget} worstCaseLoss={worstCaseLoss} />
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs tabular-nums">
          <span>
            risque{" "}
            <span className="text-foreground font-mono font-semibold" data-testid="cockpit-wcl">
              {formatEur(worstCaseLoss)}
            </span>{" "}
            / {formatEur(budget)}
          </span>
          <span className={cn("font-mono font-semibold", usedToneClass(usedPct))} data-testid="cockpit-budget-used">
            {usedPct == null ? "—" : `${usedPct.toFixed(1)}%`}
          </span>
          <span data-testid="cockpit-headroom">
            marge restante <span className="text-foreground font-mono">{formatEur(headroom)}</span>
          </span>
        </div>
      </div>

      {/* ── « Acheter maintenant » : jambe OPERATEUR forcée au tick suivant (deux clics, sans OTP) ── */}
      {operatorEntry && <OperatorBuyControl entry={operatorEntry} variant="cycle" />}

      {/* ── tenue à travers la coupure (override opérateur, deux clics, sans OTP) ── */}
      <HoldOverCloseControl cycle={cycle} stopoutPnl={stopoutPnl} />

      {/* ── financement : seulement s'il pèse ── */}
      {financing != null && financing !== 0 && (
        <div className="text-muted-foreground text-xs" data-testid="cockpit-financing">
          Financement overnight porté :{" "}
          <PnlValue value={-financing} pnlComplete synthetic={false} />
        </div>
      )}

      {error && (
        <p className="text-loss text-sm" role="alert">
          {error}
        </p>
      )}
    </Panel>
  );
}

/**
 * « TENIR LE WEEK-END » — exempte le cycle actif de la PROCHAINE coupure calendaire (flatten sauté,
 * breaker CLOSED en hold, stops garantis maintenus). Deux clics avec l'enjeu affiché (le P&L si tous
 * les stops touchaient), sans OTP ni phrase (décision owner 2026-10-26) : le geste ne passe aucun
 * ordre et se révoque librement. Le banc classe la tenue automatique NO-GO : c'est un pari
 * discrétionnaire, d'où le récapitulatif avant de confirmer.
 */
function HoldOverCloseControl({
  cycle,
  stopoutPnl,
}: {
  cycle: CycleResponse;
  stopoutPnl: number | null;
}) {
  const [hold, setHold] = useState<CycleResponse["holdOverClose"] | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // État affiché = optimiste local si on vient d'agir, sinon ce que dit le poll /current.
  const effective = hold === undefined ? (cycle.holdOverClose ?? null) : hold;

  const begin = () => {
    setErr(null);
    setOpen(true);
  };

  const confirm = async () => {
    setErr(null);
    setBusy(true);
    try {
      const h = await api.holdOverClose();
      setHold(h);
      setOpen(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErr("Pas de cycle actif, ou pas de clôture à venir.");
      else setErr("Impossible d'accorder la tenue.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setErr(null);
    setBusy(true);
    try {
      await api.revokeHoldOverClose();
      setHold(null);
    } catch {
      setErr("Impossible de révoquer la tenue.");
    } finally {
      setBusy(false);
    }
  };

  if (effective) {
    return (
      <div
        className="border-warning/40 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-card px-3 py-2 text-xs"
        data-testid="cockpit-hold"
      >
        <Moon className="text-warning size-3.5" aria-hidden />
        <span className="text-foreground font-semibold">Tient la coupure du {formatTime(effective.untilClose)}</span>
        <span className="text-muted-foreground">accordée par {effective.grantedBy}</span>
        <span className="text-muted-foreground">
          le flatten de cette clôture est suspendu, les stops garantis restent chez IG ; la tenue s'éteint à la
          réouverture qui suit
        </span>
        <button
          type="button"
          onClick={revoke}
          disabled={busy}
          className="border-border text-muted-foreground hover:text-foreground ml-auto rounded-lg border px-3 py-1"
          data-testid="cockpit-hold-revoke"
        >
          {busy ? "…" : "Annuler la tenue"}
        </button>
        {err && (
          <p className="text-loss w-full" role="alert">
            {err}
          </p>
        )}
      </div>
    );
  }

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">Coupure calendaire : le livre sera flat 15 min avant la clôture.</span>
        <button
          type="button"
          onClick={begin}
          className="border-border text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded-lg border px-3 py-1"
          data-testid="cockpit-hold-begin"
        >
          <Moon className="size-3.5" aria-hidden /> Tenir le week-end…
        </button>
      </div>
    );
  }

  return (
    <div className="border-warning/40 flex flex-col gap-2.5 rounded-lg border bg-card p-3" data-testid="cockpit-hold-form">
      <p className="text-muted-foreground text-sm">
        Porter ce livre à travers la prochaine coupure : le flatten est sauté, les stops garantis restent chez IG,
        le bot reprend le pilotage à la réouverture. Révocable à tout moment.
      </p>
      <p className="text-muted-foreground text-xs" data-testid="cockpit-hold-stake">
        Si tous les stops garantis touchaient pendant la coupure :{" "}
        <span className="text-foreground font-mono">{stopoutPnl == null ? "—" : formatEur(stopoutPnl)}</span>. Le banc
        classe la tenue automatique perdante (drawdown ×2 sur 2018-2021) : c'est un pari discrétionnaire.
      </p>
      {err && (
        <p className="text-loss text-sm" role="alert">
          {err}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={confirm}
          disabled={busy}
          className="bg-warning flex-1 rounded-lg px-4 py-2 text-sm font-bold text-black disabled:opacity-50"
          data-testid="cockpit-hold-confirm"
        >
          {busy ? "…" : "Oui, tenir le week-end"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="border-border text-muted-foreground hover:text-foreground rounded-lg border px-4 py-2 text-sm"
          data-testid="cockpit-hold-cancel"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function Kpi({
  label,
  sub,
  children,
}: {
  label: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-[0.68rem] font-medium tracking-wide uppercase">
        {label}
        {sub && <span className="ml-1.5 normal-case tracking-normal">· {sub}</span>}
      </span>
      <span className="text-foreground">{children}</span>
    </div>
  );
}

function humanStopError(reason: string): string {
  if (reason.includes("NO_ACTIVE_CYCLE")) return "Aucun cycle actif à arrêter.";
  return `Refusé : ${reason}`;
}

function usedToneClass(pct: number | null): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 90) return "text-sell";
  if (pct >= 70) return "text-warning";
  return "text-foreground";
}

function formatEur(v: number | null): string {
  if (v == null) return "—";
  return `€${v.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString("en-GB", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function formatDuration(startIso: string, endIso: string | null): string {
  const start = Date.parse(startIso);
  if (Number.isNaN(start)) return "—";
  const end = endIso ? Date.parse(endIso) : Date.now();
  if (Number.isNaN(end)) return "—";
  const secs = Math.max(0, Math.floor((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}
